import React, { useEffect, useState } from "react";
import {
  Table,
  Tag,
  Button,
  Row,
  Tooltip,
  Modal,
  Descriptions,
  Input,
  notification,
  DatePicker,
  Space,
} from "antd";
import { useLoading } from "../../context/loadingContext";
import dayjs from "dayjs";
import SearchDatePicker from "../../components/search/datePicker";
import {
  FilterOutlined,
  RedoOutlined,
  RollbackOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useAdmin } from "../../context/adminContext";
import moneyFormat from "../../functions/utils/moneyFormat";
import GetBankTransactionLog from "functions/admin/getBankTransactionLog";
import ExportBankTransactionLog from "functions/admin/exportBankTransactionLog";
import { downloadBankTransactionLogExcel } from "functions/admin/buildBankTransactionLogExcel";
import { downloadBankTransactionLogJson } from "functions/admin/toAccountingPayload";
import { btlTypeLabel } from "functions/admin/btlTypeLabel";
import { post } from "../../service/api";
import { useParams } from "react-router-dom";
import SearchAdmin from "components/search/searchAdmin";
import { bg_button, bg_shadow } from "constant/css";
import TablePagination from "components/pagination/tablePagination";

const { RangePicker } = DatePicker;

const BankTransactionLog = () => {
  const { account } = useParams();

  const { showLoading, hideLoading } = useLoading();
  const { searchFields, setSearchFields, showFilter, setShowFilter } =
    useAdmin();
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationData, setPaginationData] = useState({});
  const [refundModal, setRefundModal] = useState({ open: false, record: null });
  const [refundFromAccount, setRefundFromAccount] = useState("5570025382");
  const [refundTransferId, setRefundTransferId] = useState("");
  const [excelModal, setExcelModal] = useState(false);
  const [excelRange, setExcelRange] = useState([
    dayjs().startOf("month"),
    dayjs().endOf("day"),
  ]);
  const [excelExporting, setExcelExporting] = useState(false);

  const getNextTransferId = () => {
    const today = dayjs().format("YYYY-MM-DD");
    const saved = JSON.parse(localStorage.getItem("refund_transferid") || "{}");
    if (saved.date === today) {
      return String(saved.id).padStart(4, "0");
    }
    return "0190";
  };

  const saveAndIncrementTransferId = (currentId) => {
    const today = dayjs().format("YYYY-MM-DD");
    localStorage.setItem(
      "refund_transferid",
      JSON.stringify({ date: today, id: parseInt(currentId, 10) + 1 })
    );
  };
  useEffect(() => {
    prepareQueryStringAndSearch();
  }, [page, pageSize]);

  useEffect(() => {
    if (page === 1) prepareQueryStringAndSearch();
    else setPage(1);
  }, [searchFields, account]);
  const prepareQueryStringAndSearch = () => {
    const searchFieldsLength = Object.keys(searchFields).length;
    var queryString = ``;
    if (searchFieldsLength !== 0) {
      queryString = Object.keys(searchFields)
        .filter((key) => searchFields[key] != null && searchFields[key] !== "")
        .map((key) => {
          if (key.includes("At") || key.includes("Date")) {
            const start = dayjs(searchFields[key][0])
              .startOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
            const end = dayjs(searchFields[key][1])
              .endOf("day")
              .format("YYYY-MM-DD HH:mm:ss");
            return `${key}=${encodeURIComponent(`${start}->${end}`)}`;
          }
          return `${key}=${encodeURIComponent(searchFields[key])}`;
        })
        .join("&");
      fetchTransactionLogs(queryString, page, pageSize);
    } else fetchTransactionLogs("", page, pageSize);
  };
  const defaultColumns = [
    {
      title: (
        <SearchAdmin
          sort={false}
          title="ID"
          fieldName="id"
          width={120}
          debounceMs={1500}
          placeholder="ID..."
        />
      ),
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 120,
    },
    {
      title: <span className="text-xs">Данс</span>,
      dataIndex: "accountNumber",
      key: "accountNumber",
      width: 120,
    },
    {
      title: (
        <SearchAdmin
          sort={false}
          title="Хүлээн авагч/ шилжүүлэгч"
          fieldName="relatedAccount"
          width={160}
          debounceMs={1500}
          placeholder="Дансны дугаар..."
        />
      ),
      dataIndex: "relatedAccount",
      key: "relatedAccount",
      width: 160,
      render: (e) =>
        e ? (
          <span className="text-xs">{e}</span>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        ),
    },
    {
      title: (
        <SearchAdmin
          sort={false}
          title="Гүйлгээний утга"
          fieldName="description"
          width={300}
          debounceMs={1500}
          placeholder="Утга хайх..."
        />
      ),
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (e, row) => {
        const text = String(e).substring(0, 47);
        const isHighlighted =
          !String(text).toLowerCase().includes("xpay") &&
          !String(text).toLowerCase().includes("qpay") &&
          !String(text).toLowerCase().includes("merchant") &&
          !String(text).toLowerCase().includes("negdi");

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <Tooltip title={e}>
              <div
                style={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "100%",
                }}
              >
                {isHighlighted ? (
                  <Tag color="error" className="text-xs">
                    {text}
                  </Tag>
                ) : (
                  <span className="text-xs">{text}</span>
                )}
              </div>
            </Tooltip>
            {isHighlighted && (
              <Button
                size="small"
                danger
                icon={<RollbackOutlined />}
                onClick={() => {
                  setRefundFromAccount("5570025382");
                  setRefundTransferId(getNextTransferId());
                  setRefundModal({ open: true, record: row });
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: <span className="text-xs">Орлого</span>,
      dataIndex: "amount",
      key: "income",
      align: "right",
      width: 120,
      render: (amount) => {
        const n = parseFloat(amount);
        if (!(n > 0)) return <span className="text-xs text-gray-400">—</span>;
        return <Tag color="cyan">{moneyFormat(n)}</Tag>;
      },
    },
    {
      title: <span className="text-xs">Зарлага</span>,
      dataIndex: "amount",
      key: "expense",
      align: "right",
      width: 120,
      render: (amount) => {
        const n = parseFloat(amount);
        if (!(n < 0)) return <span className="text-xs text-gray-400">—</span>;
        return <Tag color="warning">{moneyFormat(Math.abs(n))}</Tag>;
      },
    },
    {
      title: <span className="text-xs">Төрөл</span>,
      dataIndex: "isPack",
      key: "isPack",
      width: 110,
      render: (isPack, row) => {
        const amount = parseFloat(row.amount);
        if (Number.isFinite(amount) && amount < 0) {
          const expenseType = btlTypeLabel(row);
          if (!expenseType) {
            return <span className="text-xs text-gray-400">—</span>;
          }
          return (
            <Tag
              color={expenseType === "Settle" ? "geekblue" : "volcano"}
              className="text-xs"
            >
              {expenseType}
            </Tag>
          );
        }
        if (isPack == null) {
          return <span className="text-xs text-gray-400">—</span>;
        }
        const productName = row.qpay?.items?.[0]?.productTypeName;
        return (
          <Tooltip title={productName || undefined}>
            <Tag color={isPack ? "purple" : "default"} className="text-xs">
              {isPack ? "Багц" : "Дан"}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: <span className="text-xs">Харилцагч</span>,
      dataIndex: "customers",
      key: "customers",
      width: 200,
      render: (customers, row) => {
        const list = Array.isArray(customers) ? customers : [];
        const settleAcc = row.settle?.bucketAccountNumber;
        const fallback =
          !list.length &&
          (row.direction === "OUT" || parseFloat(row.amount) < 0) &&
          (settleAcc || row.relatedAccount)
            ? [
                {
                  accountNumber: settleAcc || row.relatedAccount,
                  source: settleAcc ? "SETTLE_BUCKET" : "RELATED_ACCOUNT",
                },
              ]
            : [];
        const shown = list.length ? list : fallback;
        if (!shown.length) {
          return (
            <Tooltip title={row.linkError || undefined}>
              <span className="text-xs text-gray-400">—</span>
            </Tooltip>
          );
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {shown.map((c) => (
              <Tooltip
                key={c.accountNumber}
                title={[
                  c.name,
                  c.source === "SETTLE_BUCKET"
                    ? "Settle хүлээн авагч (bucket)"
                    : c.source === "RELATED_ACCOUNT"
                    ? "Шилжүүлсэн данс (relatedAccount)"
                    : c.productTypeName,
                  c.amount != null ? moneyFormat(c.amount) : null,
                  c.itemCount > 1 ? `${c.itemCount} мөр` : null,
                  c.description,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              >
                <Tag
                  color={
                    c.source === "SETTLE_BUCKET"
                      ? "geekblue"
                      : c.source === "RELATED_ACCOUNT"
                      ? "gold"
                      : row.linkConfidence === "LOW"
                      ? "orange"
                      : "blue"
                  }
                  className="text-xs"
                >
                  {c.name
                    ? `${c.name}${c.accountNumber ? ` (${c.accountNumber})` : ""}`
                    : c.accountNumber || "?"}
                </Tag>
              </Tooltip>
            ))}
          </div>
        );
      },
    },
    {
      title: <span className="text-xs">Шимтгэл</span>,
      key: "linkFees",
      width: 200,
      render: (_, row) => {
        const s = row.settle;
        const q = row.qpay;
        if (s) {
          return (
            <Tooltip
              title={
                <div className="text-xs space-y-0.5">
                  <div>Settle гүйлгээ: {s.transactionCount}</div>
                  <div>Merchant fee: {moneyFormat(s.sumMerchantFee)}</div>
                  <div>XPay fee: {moneyFormat(s.sumXpayFee)}</div>
                  <div>Processing fee: {moneyFormat(s.sumProcessingFee)}</div>
                  <div>After fee: {moneyFormat(s.sumAfterFeeAmount)}</div>
                  <div>Bucket #{s.transactionBucketId}</div>
                </div>
              }
            >
              <div className="text-xs leading-snug text-gray-700">
                <div>
                  <span className="text-gray-500">Merchant </span>
                  {moneyFormat(s.sumMerchantFee)}
                </div>
                <div>
                  <span className="text-gray-500">XPay </span>
                  {moneyFormat(s.sumXpayFee)}
                </div>
                <div>
                  <span className="text-gray-500">After </span>
                  {moneyFormat(s.sumAfterFeeAmount)}
                </div>
              </div>
            </Tooltip>
          );
        }
        if (q) {
          const firstItem = Array.isArray(q.items) ? q.items[0] : null;
          const bankNet =
            q.bankNetAmount != null
              ? q.bankNetAmount
              : Math.abs(parseFloat(row.amount) || 0);
          const bankCharge = q.bankCharge;
          const invoiceAmt = parseFloat(
            q.sumAmount || q.invoiceAmount || q.invoiceTotalAmount || 0
          );
          const merchantFee = parseFloat(q.sumMerchantFee || 0);
          const xpayFee = parseFloat(q.sumXpayFee || 0);
          const xpayMinusCharge =
            bankCharge != null
              ? Number((xpayFee - parseFloat(bankCharge)).toFixed(2))
              : null;
          const subMerchantAfter = Number(
            (invoiceAmt - merchantFee - xpayFee).toFixed(2)
          );
          return (
            <Tooltip
              title={
                <div className="text-xs space-y-0.5">
                  <div>Invoice #{q.invoiceId}</div>
                  <div>Item: {q.itemCount}</div>
                  {firstItem?.productTypeName && (
                    <div>Төрөл: {firstItem.productTypeName}</div>
                  )}
                  {firstItem?.accountNumber && (
                    <div>Данс: {firstItem.accountNumber}</div>
                  )}
                  {firstItem?.transactionId && (
                    <div>Tx #{firstItem.transactionId}</div>
                  )}
                  <div>Нэхэмжлэл дүн: {moneyFormat(invoiceAmt)}</div>
                  <div>Дансанд орсон: {moneyFormat(bankNet)}</div>
                  <div>Main merchant fee: {moneyFormat(q.sumMerchantFee)}</div>
                  <div>xpay_fee: {moneyFormat(q.sumXpayFee)}</div>
                  <div>
                    qpay charge:{" "}
                    {bankCharge != null ? moneyFormat(bankCharge) : "—"}
                  </div>
                  <div>
                    XPay fee − qpay charge:{" "}
                    {xpayMinusCharge != null
                      ? moneyFormat(xpayMinusCharge)
                      : "—"}
                  </div>
                  <div>
                    sub Merchant after: {moneyFormat(subMerchantAfter)}
                  </div>
                </div>
              }
            >
              <div className="text-xs leading-snug text-gray-700">
                <div>
                  <span className="text-gray-500">Нэхэмжлэл </span>
                  {moneyFormat(invoiceAmt)}
                </div>
                <div>
                  <span className="text-gray-500">Орсон </span>
                  {moneyFormat(bankNet)}
                </div>
                <div>
                  <span className="text-gray-500">XPay−CHARGE </span>
                  {xpayMinusCharge != null
                    ? moneyFormat(xpayMinusCharge)
                    : "—"}
                </div>
              </div>
            </Tooltip>
          );
        }
        return <span className="text-xs text-gray-400">—</span>;
      },
    },
    {
      title: <span className="text-xs">Invoice</span>,
      dataIndex: "invoiceId",
      key: "invoiceId",
      width: 110,
      render: (invoiceId, row) => {
        if (row.transactionBucketId) {
          return (
            <Tooltip
              title={
                row.linkError ||
                (row.linkConfidence === "HIGH"
                  ? "Settle → transactionBucket"
                  : "Settle холбоос сул")
              }
            >
              <Tag
                color={row.linkConfidence === "HIGH" ? "geekblue" : "orange"}
                className="text-xs"
              >
                B#{row.transactionBucketId}
              </Tag>
            </Tooltip>
          );
        }
        if (!invoiceId) return <span className="text-xs text-gray-400">—</span>;
        return (
            <Tooltip
              title={
                row.linkError ||
                (row.linkConfidence === "HIGH"
                  ? row.qpay
                    ? `QPay → invoice #${invoiceId} · item ${row.qpay.itemCount} · tx ${
                        row.qpay.items?.[0]?.transactionId || "—"
                      }`
                    : "QPay description → invoice"
                  : "Холбоос сул (дүн зөрүүтэй)")
              }
            >
            <Tag
              color={row.linkConfidence === "HIGH" ? "green" : "orange"}
              className="text-xs"
            >
              #{invoiceId}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: <span className="text-xs">Төлсөн хэлбэр</span>,
      key: "paymentMethodName",
      width: 130,
      render: (_, row) => {
        const name =
          row.qpay?.paymentMethodName || row.qpay?.productPaymentType;
        if (!name) return <span className="text-xs text-gray-400">—</span>;
        return <span className="text-xs">{name}</span>;
      },
    },
    {
      title: <span className="text-xs">Үлдэгдэл</span>,
      dataIndex: "remain",
      key: "remain",
      align: "right",
      render: (e) => moneyFormat(parseFloat(e)),
      width: 130,
    },
    {
      title: (
        <SearchDatePicker
          title="Гүйлгээний огноо"
          datePicker
          fieldName="trnDate"
          width={260}
        />
      ),
      dataIndex: "trnDate",
      key: "trnDate",
      width: 270,
      render: (e) => (e ? dayjs(e).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Татсан огноо",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (e) => (e ? dayjs(e).format("YYYY-MM-DD HH:mm") : "-"),
    },
  ];
  useEffect(() => {
    refresh();
  }, []);
  const fetchTransactionLogs = async (queryString, page, size) => {
    showLoading();
    const response = await GetBankTransactionLog(
      `&accountNumber=${account}&page=${page}&size=${size}&${queryString}`
    );
    hideLoading();
    if (response) {
      setData(response.data);
      setPaginationData(response.pagination);
    }
  };
  const changePage = (page) => {
    setPage(page);
  };
  const changeSize = (current, size) => {
    setPageSize(size);
  };
  const handleAccountingExport = async (kind) => {
    if (!excelRange?.[0] || !excelRange?.[1]) {
      notification.warning({ message: "Эхлэх/дуусах огноо сонгоно уу" });
      return;
    }
    const startDate = excelRange[0].startOf("day").format("YYYY-MM-DD");
    const endDate = excelRange[1].endOf("day").format("YYYY-MM-DD");
    setExcelExporting(true);
    showLoading();
    try {
      const response = await ExportBankTransactionLog({
        accountNumber: account,
        startDate,
        endDate,
      });
      if (!response?.success || !response?.data) {
        notification.error({
          message: response?.message || "Экспорт өгөгдөл авахад алдаа гарлаа",
        });
        return;
      }
      if (kind === "json") {
        downloadBankTransactionLogJson(response.data);
        notification.success({
          message: "JSON татагдлаа",
          description: `${response.data.totalCount || 0} мөр · англи key`,
        });
      } else {
        downloadBankTransactionLogExcel(response.data);
        notification.success({
          message: "Excel татагдлаа",
          description: `${response.data.totalCount || 0} мөр · сар бүрийн sheet`,
        });
      }
      setExcelModal(false);
    } catch (err) {
      notification.error({
        message:
          err.response?.data?.message || err.message || "Экспорт алдаа гарлаа",
      });
    } finally {
      setExcelExporting(false);
      hideLoading();
    }
  };

  const refresh = () => {
    setPage(1);
    setSearchFields({});
    fetchTransactionLogs("", 1, pageSize);
  };
  const toggleFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleRefundSubmit = async () => {
    const rec = refundModal.record;
    showLoading();
    try {
      // 2. Bearer token → шилжүүлэг
      // const payload = {
      //   fromAccount: refundFromAccount,
      //   toAccount: rec.relatedAccount,
      //   toCurrency: "MNT",
      //   amount: Math.abs(parseFloat(rec.amount)),
      //   description: "Aldaatai hiisen guilgee tul butsaav eSukh.mn",
      //   currency: "MNT",
      //   loginName: "contact@smartbilling.mn",
      //   tranPassword: "@BillinG!@#321",
      //   transferid: refundTransferId,
      //   URL: "domestic",
      // };
      const payload = {
        fromAccount: refundFromAccount,
        toAccount: rec.relatedAccount,
        amount: Math.abs(parseFloat(rec.amount)),
        refundTransferId: refundTransferId,
      }

      await post("/admin/transfer/domestic", payload);

      saveAndIncrementTransferId(refundTransferId);
      setRefundModal({ open: false, record: null });
      notification.success({ message: "Гүйлгээ амжилттай буцаагдлаа" });
    } catch (err) {
      notification.error({
        message: err.response?.data?.message || err.message || "Алдаа гарлаа",
      });
    } finally {
      hideLoading();
    }
  };

  return (
    <div className="vatMerchantListontainer mt-3">
      <Row style={{ gap: 10 }}>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} rounded-xl ${bg_shadow}`}
            onClick={refresh}
          >
            <RedoOutlined />
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type={"primary"}
            className={`spayDefaultButton ${bg_button} rounded-xl ${bg_shadow} ${!showFilter && "spayDefaultButtonInactive"
              }`}
            onClick={toggleFilter}
          >
            <FilterOutlined />
          </Button>
        </div>
        <div className="addButtonMerchant">
          <Button
            type="primary"
            className={`${bg_button} rounded-xl ${bg_shadow}`}
            icon={<FileExcelOutlined />}
            onClick={() => setExcelModal(true)}
          >
            Excel / JSON
          </Button>
        </div>
      </Row>
      <div className="tableContainer">
        <Table
          columns={defaultColumns}
          rowKey={(record) => record.id}
          scroll={{
            x: "max-content",
            y: "calc(100vh - 38vh)",
          }}
          size="small"
          pagination={false}
          dataSource={data}
        // summary={(pageData) => {
        //   return (
        //     <Table.Summary fixed>
        //       <Table.Summary.Row className="summaryRow">
        //         <Table.Summary.Cell index={0} colSpan={2} align="right">
        //           Нийт дүн:
        //         </Table.Summary.Cell>
        //         <Table.Summary.Cell index={1} colSpan={2} align="right">
        //           {summary ? moneyFormat(summary.totalAmount) : 0}
        //         </Table.Summary.Cell>
        //       </Table.Summary.Row>
        //     </Table.Summary>
        //   );
        // }}
        ></Table>
        <TablePagination
          paginationData={paginationData}
          page={page}
          changePage={changePage}
          changeSize={changeSize}
        />
      </div>
      <Modal
        title="Нягтлан бодох бүртгэл — Excel / JSON"
        open={excelModal}
        onCancel={() => !excelExporting && setExcelModal(false)}
        confirmLoading={excelExporting}
        destroyOnClose
        footer={[
          <Button key="cancel" disabled={excelExporting} onClick={() => setExcelModal(false)}>
            Болих
          </Button>,
          <Button
            key="json"
            loading={excelExporting}
            onClick={() => handleAccountingExport("json")}
          >
            JSON татах
          </Button>,
          <Button
            key="excel"
            type="primary"
            loading={excelExporting}
            onClick={() => handleAccountingExport("excel")}
          >
            Excel татах
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div className="text-xs text-gray-600">
            Данс: <b>{account}</b>
            <br />
            Excel болон JSON ижил англи багана/талбартай. Сараар sheet + «Тойм».
          </div>
          <RangePicker
            style={{ width: "100%" }}
            value={excelRange}
            onChange={(v) => setExcelRange(v)}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        </Space>
      </Modal>
      <Modal
        title="Гүйлгээ буцаах"
        open={refundModal.open}
        onCancel={() => setRefundModal({ open: false, record: null })}
        footer={
          <Button danger type="primary" onClick={handleRefundSubmit}>
            Буцаах
          </Button>
        }
        width={560}
      >
        {refundModal.record && (() => {
          const rec = refundModal.record;
          const amount = Math.abs(parseFloat(rec.amount));
          return (
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label="fromAccount">
                <Input
                  value={refundFromAccount}
                  onChange={(e) => setRefundFromAccount(e.target.value)}
                  size="small"
                />
              </Descriptions.Item>
              <Descriptions.Item label="toAccount">{rec.relatedAccount}</Descriptions.Item>
              <Descriptions.Item label="toCurrency">MNT</Descriptions.Item>
              <Descriptions.Item label="amount">{amount}</Descriptions.Item>
              <Descriptions.Item label="description">Aldaatai hiisen guilgee tul butsaav eSukh.mn</Descriptions.Item>
              <Descriptions.Item label="currency">MNT</Descriptions.Item>
              <Descriptions.Item label="loginName">contact@smartbilling.mn</Descriptions.Item>
              <Descriptions.Item label="tranPassword">@BillinG!@#321</Descriptions.Item>
              <Descriptions.Item label="transferid">
                <Input
                  value={refundTransferId}
                  onChange={(e) => setRefundTransferId(e.target.value)}
                  size="small"
                />
              </Descriptions.Item>
              <Descriptions.Item label="URL">domestic</Descriptions.Item>
            </Descriptions>
          );
        })()}
      </Modal>
    </div>
  );
};

export default BankTransactionLog;
